(ns ui.elevenlabs.voice-list
  (:require [parts.replicant.query :as query]))

;; Define a vector of supported languages as per the Elevenlabs API.
(def supported-languages
  [["Arabic" "ar"]
   ["Chinese" "zh"]
   ["Czech" "cs"]
   ["Danish" "da"]
   ["Dutch" "nl"]
   ["English" "en"]
   ["Finnish" "fi"]
   ["French" "fr"]
   ["German" "de"]
   ["Hindi" "hi"]
   ["Hungarian" "hu"]
   ["Indonesian" "id"]
   ["Italian" "it"]
   ["Japanese" "ja"]
   ["Korean" "ko"]
   ["Norwegian" "no"]
   ["Polish" "pl"]
   ["Portuguese" "pt"]
   ["Romanian" "ro"]
   ["Russian" "ru"]
   ["Spanish" "es"]
   ["Swedish" "sv"]
   ["Turkish" "tr"]
   ["Ukrainian" "uk"]
   ["Vietnamese" "vi"]])

(def query
  {:query/kind :query/elevenlabs-voice-list})

(defn voice-list
  [state]
  (let [q (cond-> query
            (::search state)   (assoc-in [:query/data :search] (::search state))
            (::language state) (assoc-in [:query/data :language] (::language state)))
        result (query/get-result state q)
        current-url (:audio/current-url state)]
    [:div {:class ["min-h-screen" "bg-gray-50" "p-6"]}
     ;; Page heading
     [:h1 {:class ["text-2xl" "font-bold" "mb-6"]} "Voices"]

     ;; Combined Search and Language filter container
     [:div {:class ["max-w-md" "mx-auto" "mb-12"]}

      ;; Language combobox dynamically generated from supported-languages
      [:div {:class ["mb-4"]}
       [:select {:id "language-select"
                 :class ["mt-1" "block" "w-full" "pl-3" "pr-10" "py-2" "text-base" "border" "border-gray-300" "focus:outline-none" "focus:ring-blue-500" "focus:border-blue-500" "sm:text-sm" "rounded-md"]
                 :on {:change [[:store/assoc ::language-box :event/target.value]]}}
        [:option {:value ""} "Any language"]
        (for [[lang-name lang-code] supported-languages]
          ^{:key lang-code} [:option {:value lang-code} lang-name])]]

      ;; Search input field with updated styling
      [:label {:for "default-search" :class ["mb-2" "text-sm" "font-medium" "text-gray-900" "sr-only"]}
       "Search"]
      (let [search-query (-> query
                             (assoc-in [:query/data :search] (::search-box state))
                             (assoc-in [:query/data :language] (::language-box state)))
            search-actions [[:store/assoc ::search (::search-box state)]
                            [:store/assoc ::language (::language-box state)]
                            [:data/query search-query]]]
        [:div {:class ["relative" "mb-4"]}
         [:div {:class ["absolute" "inset-y-0" "start-0" "flex" "items-center" "ps-3" "pointer-events-none"]}
          [:svg {:class ["w-4" "h-4" "text-gray-500"]
                 :aria-hidden "true"
                 :xmlns "http://www.w3.org/2000/svg"
                 :fill "none"
                 :viewBox "0 0 20 20"}
           [:path {:stroke "currentColor"
                   :stroke-linecap "round"
                   :stroke-linejoin "round"
                   :stroke-width "2"
                   :d "m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"}]]]
         [:input {:type "search"
                  :id "default-search"
                  :class ["block" "w-full" "p-4" "ps-10" "text-sm" "text-gray-900" "border" "border-gray-300" "rounded-lg" "focus:ring-blue-500" "focus:border-blue-500"]
                  :placeholder "Search voices"
                  :on {:change [[:store/assoc ::search-box :event/target.value]]
                       :keyup (cons [:js/on-enter]
                                    search-actions)}}]
         ;; Search button dispatches both search and language queries
         [:button {:class ["text-white" "absolute" "end-2.5" "bottom-2.5" "bg-blue-700" "hover:bg-blue-800" "focus:ring-4" "focus:outline-none" "focus:ring-blue-300" "font-medium" "rounded-md" "text-sm" "px-4" "py-2"]
                   :on {:click search-actions}}
          "Search"]])

      ;; Voices list container
      [:ul {:class ["space-y-4"]}
       (for [voice (:voices result)]
         ^{:key (:voice_id voice)}
         [:li {:class ["flex" "items-center" "p-4" "bg-white" "rounded-lg" "shadow"]}
          ;; Avatar with hover overlay and audio toggle
          [:div {:class ["group" "relative" "w-10" "h-10" "mr-4" "cursor-pointer" "overflow-hidden" "rounded-full" "flex-shrink-0"]
                 :style {:aspect-ratio "1 / 1"}
                 :on {:click [[:audio/toggle (:preview_url voice)]]}}
           ;; Avatar base: display first letter of voice name
           [:div {:class ["flex" "items-center" "justify-center" "w-full" "h-full" "bg-blue-500" "text-white"]}
            (subs (or (:name voice) " ")
                  0
                  1)]
           ;; Hover overlay: shows either a play or a pause icon.
           [:div {:class ["absolute" "inset-0" "rounded-full" "bg-black" "flex" "items-center" "justify-center" "opacity-0" "group-hover:opacity-100" "transition-opacity" "duration-150"]}
            (if (= (:preview_url voice) current-url)
              ;; Pause icon: two vertical rectangles.
              [:svg {:xmlns "http://www.w3.org/2000/svg"
                     :viewBox "0 0 24 24"
                     :class ["w-6" "h-6"]
                     :fill "white"}
               [:rect {:x "6" :y "4" :width "4" :height "16"}]
               [:rect {:x "14" :y "4" :width "4" :height "16"}]]
              ;; Play icon: a triangle.
              [:svg {:xmlns "http://www.w3.org/2000/svg"
                     :viewBox "0 0 24 24"
                     :class ["w-6" "h-6"]
                     :fill "white"}
               [:polygon {:points "5,3 19,12 5,21"}]])]]
          ;; Text content
          [:div
           [:div {:class ["font-semibold" "text-gray-800" "cursor-pointer"]
                  :on {:click [[:audio/pause]
                               [:store/assoc-in
                                [:elevenlabs :public-user-id]
                                (:public_owner_id voice)]
                               [:store/assoc-in
                                [:elevenlabs :voice-id]
                                (:voice_id voice)]
                               [:store/assoc-in
                                [:elevenlabs :voice-name]
                                (:name voice)]
                               [:ui/modal-open
                                :modals/elevenlabs-generate-speech
                                {} ;; params map
                                ]]}}
            (:name voice)]
           [:div {:class ["flex" "flex-wrap" "gap-2" "text-sm" "text-gray-600" "mt-1"]}
            (for [tag [(:use_case voice) (:accent voice) (:descriptive voice)]
                  :when tag]
              ^{:key tag}
              [:span {:class ["px-2" "py-1" "bg-gray-200" "rounded-full"]}
               tag])]]])]]]))

(defn render [state]
  [:div
   (voice-list state)])

(def modal
  {:modal-id :modals/elevenlabs-voice-list
   :on-load (fn [_state]
              [[:data/query query]])
   :render #'render})
